import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { DEFAULT_CITATION_STYLE, resolveCslStyleId } from "@/lib/citationStyles";
import { CitationStylePicker } from "@/components/settings/CitationStylePicker";
import type { Workspace } from "@/hooks/useWorkspace";

type Props = { workspace: Workspace };

/**
 * Workspace-wide default citation style. Now backed by the full CSL
 * catalog via CitationStylePicker; the column accepts any
 * `^[a-z0-9-]+$` id. Per-source overrides were retired 2026-05-10 —
 * the default applies everywhere; per-Piece overrides live in the
 * Bibliography tab.
 */
export const CitationSettingsCard = ({ workspace }: Props) => {
  const [workspaceStyle, setWorkspaceStyle] = useState<string>(DEFAULT_CITATION_STYLE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("default_citation_style")
        .eq("id", workspace.id)
        .maybeSingle();
      if (cancelled) return;
      const ws = (data as { default_citation_style?: string | null } | null)?.default_citation_style;
      setWorkspaceStyle(ws ? resolveCslStyleId(ws) : DEFAULT_CITATION_STYLE);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspace.id]);

  const saveWorkspaceStyle = async (next: string) => {
    if (!next) return;
    const prev = workspaceStyle;
    setWorkspaceStyle(next);
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ default_citation_style: next })
      .eq("id", workspace.id);
    setSaving(false);
    if (error) {
      setWorkspaceStyle(prev);
      toast.error(error.message);
      return;
    }
    toast.success("Default citation style updated");
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Citation style</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Default style for references rendered in long-form drafts. Search 3,000+ journal,
          university, and society styles — or pick from popular defaults. You can override
          this at the Piece level from the composer's Bibliography tab.
        </p>

        <div className="space-y-2">
          <Label htmlFor={`ws-citestyle-${workspace.id}`}>Workspace default</Label>
          {loading ? (
            <Skeleton className="h-10 w-full max-w-sm" />
          ) : (
            <div id={`ws-citestyle-${workspace.id}`} className="max-w-sm">
              <CitationStylePicker
                value={workspaceStyle}
                onChange={(v) => void saveWorkspaceStyle(v)}
                workspaceId={workspace.id}
                disabled={saving}
                triggerClassName="w-full"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
